import { useMemo, useState } from 'react';
import { Modal } from '../components/Modal';
import { StatusBadge } from '../components/ui/StatusBadge';
import type { PlanOption, UsageLimit } from './api';

// Fase 25B: cambio de plan con confirmacion explicita (nunca dropdown silencioso). Muestra plan actual,
// plan nuevo, uso actual y limites del plan nuevo; advierte si el negocio quedara sobre el limite (sin
// desactivar recursos). Requiere motivo (10-500).
export function PlanChangeModal({ plans, current, usage, busy, onClose, onConfirm }: {
  plans: PlanOption[];
  current: string | null;
  usage: { plan: { code: string; name: string }; stores: UsageLimit; users: UsageLimit; entitlements: Array<{ key: string; label: string; kind: 'INTEGER' | 'BOOLEAN'; unlimited: boolean; limit: number | null; enabled: boolean | null }> };
  busy: boolean;
  onClose: () => void;
  onConfirm: (planCode: string, reason: string) => void;
}) {
  const [planCode, setPlanCode] = useState(current ?? plans[0]?.code ?? '');
  const [reason, setReason] = useState('');
  const valid = planCode && reason.trim().length >= 10 && reason.trim().length <= 500 && planCode !== current;

  // Aviso de over-limit: no conocemos los limites del plan destino aqui (el backend es autoritativo),
  // pero si el negocio YA esta sobre el limite actual, lo advertimos; el backend permite el cambio.
  const alreadyOver = usage.stores.overLimit || usage.users.overLimit;
  const currentName = useMemo(() => plans.find((p) => p.code === current)?.name ?? current ?? '—', [plans, current]);

  return (
    <Modal
      title="Cambiar plan"
      isBusy={busy}
      onClose={onClose}
      footer={<>
        <button className="button button--ghost" disabled={busy} onClick={onClose} type="button">Cancelar</button>
        <button className="button button--primary" disabled={busy || !valid} onClick={() => onConfirm(planCode, reason.trim())} type="button">Confirmar cambio</button>
      </>}
    >
      <dl className="platform-kv">
        <div><dt>Plan actual</dt><dd>{currentName}</dd></div>
        <div><dt>Uso actual</dt><dd>Tiendas {usage.stores.unlimited ? `${usage.stores.used} / ∞` : `${usage.stores.used} / ${usage.stores.limit}`} · Usuarios {usage.users.unlimited ? `${usage.users.used} / ∞` : `${usage.users.used} / ${usage.users.limit}`}</dd></div>
      </dl>
      <label className="field"><span>Nuevo plan</span>
        <select value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
          {plans.map((p) => <option key={p.code} value={p.code}>{p.name}{p.code === current ? ' (actual)' : ''}</option>)}
        </select>
      </label>
      {alreadyOver && <div className="alert alert--warning" role="alert">Este negocio ya está por encima del límite. Al cambiar de plan podría seguir por encima del límite y no podrá aumentar ese recurso hasta volver dentro del límite. No se eliminarán tiendas ni usuarios.</div>}
      {planCode === current && <p className="platform-muted"><StatusBadge variant="info">Sin cambios</StatusBadge> Selecciona un plan distinto al actual.</p>}
      <label className="field"><span>Motivo * (10–500 caracteres)</span>
        <textarea value={reason} rows={3} maxLength={500} onChange={(e) => setReason(e.target.value)} />
      </label>
    </Modal>
  );
}
